import { isArray } from 'lodash';
import { isEmpty } from '@app/utils';
import endpoints from '@app/constants/endpoints';
import queryString from 'query-string';
import {
  APPLICATION_AND_MATERIAL_FACET_ID,
  UOM
} from '@app/constants/commonConstants';
import { handleSubspecifications } from '@app/store/plp/utils';

export const normalizeHashFacets = facets => {
  if (Array.isArray(facets) || !facets) {
    return facets;
  }
  return [facets];
};

export const createURLWithParams = (params = {}) => {
  const queryParams = queryString.stringify(params, { encode: false });

  return `${endpoints.PRODUCT_SEARCH}?${queryParams}`;
};

/**
 * Function to handle attributes in active facets
 * @param {object[]} products array of facets
 * @param {object} facetsAsObject facets as object
 * @returns array of modified facets
 */

const getActiveFacets = (facets, facetsAsObject) => {
  return facets.reduce((acc, f) => {
    const addFacet = facet => {
      const decodedFacet = decodeURIComponent(facet.facet);
      if (facetsAsObject[decodedFacet]) {
        acc.push({
          facet: decodedFacet,
          label: facet.label,
          uom: f.uom ?? UOM.null
        });
      }
    };
    if (f.facetId === APPLICATION_AND_MATERIAL_FACET_ID) {
      handleSubspecifications(f.value).forEach(value => {
        addFacet(value);
      });
    }
    f.value.forEach(value => {
      addFacet(value);
    });
    return acc;
  }, []);
};

/**
 * Function to convert url hash to params object to store in redux for plp domain
 * @param {object} param
 * @param {object} param.parsedHash object of parsed hash from url
 * @param {object} param.data object of data returned from plpDetail fetch
 * @returns object to store in redux for plp domain
 */

export const getParamsForReduxFromHash = ({ parsedHash, data }) => {
  const { filterByEquipment, facets, orderBy } = parsedHash;
  const params = {
    filterByEquipment: filterByEquipment ?? true,
    ...(orderBy ? { orderBy } : {})
  };
  if (!isEmpty(facets) && !isEmpty(data.facets)) {
    const normalizedFacets = Array.isArray(facets) ? facets : [facets];
    const facetsAsObject = normalizedFacets.reduce((acc, f) => {
      acc[f] = true;
      return acc;
    }, {});
    params.activeFacets = getActiveFacets(data.facets, facetsAsObject);
    if (
      !isEmpty(params.activeFacets) &&
      params.activeFacets.some(f => f.uom === UOM.metric)
    ) {
      params.unitOfMeasure = UOM.metric;
    }
  }
  return params;
};

/**
 * This function is meant to extract the product's uniqueId
 * @param {object[]} products array of products as objects
 * @returns array with all the uniqueIds of the products
 */
export const extractProductsIds = products => {
  if (isArray(products) && !isEmpty(products)) {
    return products.map(({ identifier }) => identifier);
  } else {
    return [];
  }
};

/**
 * This function returns the reduced factes with their
 * corresponding UOM and facetId
 * @param {object[]} facets array of facets as objects
 * @returns facet array ordered
 */
export const getFacetsWithUOM = (facets = []) => {
  return facets.map(facet => {
    return {
      ...facet,
      facetGroupShouldBeDisabled: facet.value?.every(
        ({ count }) => count === 0
      ),
      value: facet.value.map(({ count, facet: subfacet, label, values }) => ({
        count,
        facet: subfacet,
        label,
        uom: facet.uom ?? UOM.null,
        facetId: facet.facetId,
        groupLabel: facet.label,
        values
      }))
    };
  });
};

/**
 * This function filter the array of products
 * taking into account the partNumber as the payload
 * @param {string[]} productCardExpandedArr array of products partNumbers
 * @param {string} payload string of the product partNumber
 * @returns filtered productCardArr
 */
export const filteredProductsCardsArr = (productCardExpandedArr, payload) => {
  if (!payload) {
    return [];
  }

  const foundElement = productCardExpandedArr.find(
    partNumber => partNumber === payload
  );
  if (foundElement) {
    return productCardExpandedArr.filter(partNumber => partNumber !== payload);
  }
  return [...productCardExpandedArr, payload];
};

/**
 * @param {Array<any>} products  array of product cards
 * @param {string} uom  current unit of measure
 * @returns {Array<string>} array of filtered specification product cards attributes
 */

export const filterSpecificationProductCardsByUom = (attributes, uom) => {
  if (uom !== null) {
    const oppositeUnitOfMeasure = uom === UOM.us ? UOM.metric : UOM.us;
    return attributes?.filter(attr => attr?.uom !== oppositeUnitOfMeasure);
  }
  return attributes?.filter(
    attr => attr?.uom !== UOM.us && attr?.uom !== UOM.metric
  );
};
